import React, { useState, useEffect } from 'react';
import { X, Star, Check, AlertCircle, ExternalLink, Play, ChevronDown, ChevronUp, MessageCircle, Send } from 'lucide-react';
import { COLORS, CATEGORIES } from '@/data';
import { VideoPlayer } from './ui';
import { generateId } from '@/lib/utils';
import { VERDICTS } from '@/lib/submissions';
import type { SubmittedClip, SubmittedClipReview, SubmissionVerdict, LoadBoostProfile, MaleAppearanceProfile, Creator, ReviewerQuestion } from '@/types';

interface SubmissionReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  submission: SubmittedClip;
  creator: Creator | undefined;
  reviewerId: string;
  reviewerCode?: string;
  onSave: (updatedSubmission: SubmittedClip) => Promise<SubmittedClip | null | void>;
}

export const SubmissionReviewModal: React.FC<SubmissionReviewModalProps> = ({
  isOpen,
  onClose,
  submission,
  creator,
  reviewerId,
  reviewerCode,
  onSave,
}) => {
  // Review form state
  const [review, setReview] = useState<Partial<SubmittedClipReview>>({
    reviewerId,
    reviewedAt: new Date().toISOString(),
    onBrand: false,
    showsProduct: false,
    claimsCompliant: false,
    qualityRating: 0,
    wouldTryProduct: 'maybe',
    wouldShowPartner: 'maybe',
    wouldShareWithFriend: 'maybe',
    enjoyedIt: 'neutral',
    arousedThem: 'not-really',
    howItMadeThemFeel: '',
    intrigues: '',
    wouldPayToView: 'maybe',
    wouldSubscribe: 'maybe',
    wantMoreFromCreator: 'maybe',
    wouldWatchMaleSolo: 'maybe',
    suggestedCategories: [],
    notes: '',
    revisionRequests: '',
    creatorSuggestions: '',
    followUpSuggestions: [],
  });

  // Load Boost specific fields
  const [loadBoostProfile, setLoadBoostProfile] = useState<LoadBoostProfile>({
    consistencyType: 'mixed',
    finishLocation: '',
    standoutNotes: '',
  });

  // Male appearance fields (reviewer assessment)
  const [maleAppearance, setMaleAppearance] = useState<MaleAppearanceProfile>({
    cockSize: 'average',
    hardness: 'hard',
    notes: '',
  });

  // Reviewer-editable submission fields
  const [gender, setGender] = useState<'male' | 'female' | 'couple' | ''>(submission.gender || '');
  const [standoutNotes, setStandoutNotes] = useState(submission.standoutNotes || '');
  const [productsTaking, setProductsTaking] = useState(submission.productsTaking || '');

  // Video expanded state
  const [videoExpanded, setVideoExpanded] = useState(true);

  // Questions for admin about this submission
  const [newQuestion, setNewQuestion] = useState('');
  const [questions, setQuestions] = useState<ReviewerQuestion[]>(
    (submission.reviewerQuestions || []).filter(q => q.reviewerId === reviewerId || (reviewerCode && q.reviewerId === reviewerCode))
  );
  const [savedThisSession, setSavedThisSession] = useState(false);
  const [savedPostRevealReaction, setSavedPostRevealReaction] = useState(false);
  const [savingReview, setSavingReview] = useState(false);
  const [savingReflection, setSavingReflection] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Match reviewer by id OR code (some reviews may have been saved with code instead of id)
  const isMyReview = (r: SubmittedClipReview) =>
    r.reviewerId === reviewerId || (reviewerCode && r.reviewerId === reviewerCode);

  // Helper to get all reviews (from reviews array + legacy review field)
  const getAllReviews = (sub: SubmittedClip): SubmittedClipReview[] => {
    const revs = [...(sub.reviews || [])];
    // Migrate legacy single review if not already in array
    if (sub.review && !revs.some(r => r.reviewerId === sub.review!.reviewerId)) {
      revs.push(sub.review);
    }
    return revs;
  };

  const allReviews = getAllReviews(submission);
  const currentSavedReview = allReviews.find(r => isMyReview(r));
  const otherReviews = allReviews.filter(r => !isMyReview(r));
  // Other reviews stay hidden until you commit a verdict — a draft doesn't unlock them
  const canSeeCommunityFeedback = savedThisSession || !!currentSavedReview?.verdict;
  const hasSavedPostRevealReaction = savedPostRevealReaction || !!currentSavedReview?.postRevealChangedMind;
  const needsPostRevealReflection = canSeeCommunityFeedback && otherReviews.length > 0 && !hasSavedPostRevealReaction;

  // Initialize from existing review for this reviewer
  useEffect(() => {
    const allRevs = getAllReviews(submission);
    const myReview = allRevs.find(r => isMyReview(r));
    if (myReview) {
      setReview({
        ...myReview,
        reviewerId,
        reviewedAt: new Date().toISOString(),
      });
    } else {
      // Reset to defaults when no prior review exists for this reviewer
      setReview({
        reviewerId,
        reviewedAt: new Date().toISOString(),
        onBrand: false,
        showsProduct: false,
        claimsCompliant: false,
        qualityRating: 0,
        wouldTryProduct: 'maybe',
        wouldShowPartner: 'maybe',
        wouldShareWithFriend: 'maybe',
        enjoyedIt: 'neutral',
        arousedThem: 'not-really',
        howItMadeThemFeel: '',
        intrigues: '',
        wouldPayToView: 'maybe',
        wouldSubscribe: 'maybe',
        wantMoreFromCreator: 'maybe',
        wouldWatchMaleSolo: 'maybe',
        suggestedCategories: [],
        notes: '',
        revisionRequests: '',
        creatorSuggestions: '',
        followUpSuggestions: [],
      });
    }
    // Only prefill submission-level fields when editing own existing review;
    // otherwise start fresh so each reviewer gives an independent assessment
    if (myReview) {
      setLoadBoostProfile(submission.loadBoostProfile || {
        consistencyType: 'mixed',
        finishLocation: '',
        standoutNotes: '',
      });
      setMaleAppearance(submission.maleAppearance || {
        cockSize: 'average',
        hardness: 'hard',
        notes: '',
      });
      setStandoutNotes(submission.standoutNotes || '');
    } else {
      setLoadBoostProfile({
        consistencyType: 'mixed',
        finishLocation: '',
        standoutNotes: '',
      });
      setMaleAppearance({
        cockSize: 'average',
        hardness: 'hard',
        notes: '',
      });
      setStandoutNotes('');
    }
    // Gender and products are clip metadata — always load from submission
    setGender(submission.gender || '');
    setProductsTaking(submission.productsTaking || '');
    // Load this reviewer's questions
    setQuestions((submission.reviewerQuestions || []).filter(q => q.reviewerId === reviewerId || (reviewerCode && q.reviewerId === reviewerCode)));
    setNewQuestion('');
    setSavedThisSession(false);
    setSavedPostRevealReaction(!!myReview?.postRevealChangedMind);
    setSavingReview(false);
    setSavingReflection(false);
    setSaveError('');
  }, [submission, reviewerId]);

  // Build updated reviews array with this reviewer's review
  const buildUpdatedReviews = (thisReview: SubmittedClipReview): SubmittedClipReview[] => {
    const existing = getAllReviews(submission);
    const otherReviews = existing.filter(r => !isMyReview(r));
    return [...otherReviews, thisReview];
  };

  // Merge this reviewer's questions with other reviewers' questions
  const buildQuestions = (): ReviewerQuestion[] => {
    const otherQuestions = (submission.reviewerQuestions || []).filter(q => q.reviewerId !== reviewerId);
    return [...otherQuestions, ...questions];
  };

  const buildSubmission = (status: SubmittedClip['status'], thisReview: SubmittedClipReview): SubmittedClip => {
    // Attach maleAppearance and loadBoostProfile to the individual review too
    const reviewWithAssessment: SubmittedClipReview = {
      ...thisReview,
      maleAppearance: (gender === 'male' || gender === 'couple') ? maleAppearance : undefined,
      loadBoostProfile: submission.product === 'Load Boost' ? loadBoostProfile : undefined,
    };
    return {
      ...submission,
      status,
      review: reviewWithAssessment, // Keep legacy field for backward compat
      reviews: buildUpdatedReviews(reviewWithAssessment),
      reviewerQuestions: buildQuestions(),
      // Only update submission-level fields that are truly submission metadata
      gender: gender || undefined as SubmittedClip['gender'],
      productsTaking: productsTaking || undefined,
      // Don't overwrite submission standoutNotes with reviewer's — keep original
      // Don't overwrite submission-level maleAppearance/loadBoostProfile — these are per-review now
    };
  };

  const markReviewSaved = (saved: SubmittedClip | null | void) => {
    if (saved !== null) {
      setSavedThisSession(true);
      setSaveError('');
      const savedSubmission = saved || submission;
      const savedReviews = getAllReviews(savedSubmission);
      const savedMyReview = savedReviews.find(r => isMyReview(r));
      const savedOtherReviews = savedReviews.filter(r => !isMyReview(r));
      if (savedOtherReviews.length > 0 && !savedMyReview?.postRevealChangedMind) {
        setVideoExpanded(false);
        return;
      }
      onClose();
      return;
    }
    setSaveError('That did not save. Please try again.');
  };

  // Reviewers recommend; only the admin sets Approved / Needs Revision / Rejected.
  // A reviewer's first save just moves a new submission into review.
  const reviewerStatus: SubmittedClip['status'] = submission.status === 'Submitted' ? 'Under Review' : submission.status;

  const handleSaveDraft = async () => {
    setSavingReview(true);
    setSaveError('');
    const saved = await onSave(buildSubmission(reviewerStatus, review as SubmittedClipReview));
    setSavingReview(false);
    if (saved === null) setSaveError('That did not save. Please try again.');
    else onClose();
  };

  const handleSubmitReview = async () => {
    if (!review.verdict) {
      setSaveError('Pick your verdict: approve, needs revisions or reject.');
      return;
    }
    if (review.verdict === 'revise' && !review.revisionRequests?.trim()) {
      setSaveError('Add what needs changing under Revision requests.');
      return;
    }
    setSavingReview(true);
    setSaveError('');
    const saved = await onSave(buildSubmission(reviewerStatus, { ...review, reviewedAt: new Date().toISOString() } as SubmittedClipReview));
    markReviewSaved(saved);
    setSavingReview(false);
  };

  const setVerdict = (verdict: SubmissionVerdict) => {
    setReview({ ...review, verdict });
    setSaveError('');
  };

  const handleSavePostRevealReaction = async () => {
    if (!review.postRevealChangedMind) {
      alert('Please choose whether seeing other reviews changed how you feel');
      return;
    }
    setSavingReflection(true);
    setSaveError('');
    const saved = await onSave(buildSubmission(submission.status, { ...review, reviewedAt: currentSavedReview?.reviewedAt || new Date().toISOString() } as SubmittedClipReview));
    if (saved !== null) {
      setSavedPostRevealReaction(true);
      setSaveError('');
    } else {
      setSaveError('Reflection did not save. Please try again.');
    }
    setSavingReflection(false);
  };

  const isPromoted = !!submission.promotedToClipId;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
      <div className="bg-zinc-800 rounded-2xl w-full max-w-4xl border border-zinc-700 max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-zinc-800 border-b border-zinc-700 p-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-white">Review Submission</h2>
            <p className="text-zinc-400 text-sm">{submission.clipTitle}</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto">
          {!canSeeCommunityFeedback && (
            <div className="bg-lime-500/10 border border-lime-500/30 rounded-xl p-4">
              <h3 className="text-lime-400 font-semibold mb-1">Blind review mode</h3>
              <p className="text-zinc-300 text-sm">
                You are seeing the clip and submission details only. Other reviewer ratings, notes, and request signals unlock after you save your own review.
              </p>
            </div>
          )}

          {/* Video Section */}
          <div className="bg-zinc-900 rounded-xl border border-zinc-700 overflow-hidden">
            <button
              onClick={() => setVideoExpanded(!videoExpanded)}
              className="w-full p-3 flex items-center justify-between hover:bg-zinc-800/50"
            >
              <span className="text-white font-medium flex items-center gap-2">
                <Play className="w-4 h-4" /> Video Preview
              </span>
              {videoExpanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
            </button>
            {videoExpanded && (
              <div className="p-4 pt-0">
                <VideoPlayer url={submission.contentLink} />
                {false && (
                  <div className="aspect-video bg-zinc-800 rounded-lg flex items-center justify-center">
                    <a
                      href={submission.contentLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-blue-400 hover:underline"
                    >
                      <ExternalLink className="w-5 h-5" /> Open in new tab
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Submission Info */}
          <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-700">
            <h3 className="text-white font-semibold mb-3">Submission Info</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-zinc-500">Product</p>
                <p className="text-white font-medium">
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    submission.product === 'Load Boost' ? 'bg-blue-500/20 text-blue-400' : 'bg-pink-500/20 text-pink-400'
                  }`}>
                    {submission.product}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-zinc-500">Gender</p>
                <select
                  value={gender}
                  onChange={e => setGender(e.target.value as typeof gender)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-white text-sm"
                >
                  <option value="">Not Set</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="couple">Couple</option>
                </select>
              </div>
              <div>
                <p className="text-zinc-500">Order ID</p>
                <p className="text-white font-mono text-xs">{creator?.orderId || 'N/A'}</p>
              </div>
              <div>
                <p className="text-zinc-500">Submitted</p>
                <p className="text-white">{new Date(submission.dateSubmitted).toLocaleDateString()}</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-zinc-700">
              <label className="block text-zinc-500 text-sm mb-1">Products Taking</label>
              <input
                type="text"
                value={productsTaking}
                onChange={e => setProductsTaking(e.target.value)}
                placeholder="e.g., Load Boost, Soaking Wet"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
            <div className="mt-3 pt-3 border-t border-zinc-700">
              <label className="block text-zinc-500 text-sm mb-1">Standout Notes</label>
              <textarea
                value={standoutNotes}
                onChange={e => setStandoutNotes(e.target.value)}
                placeholder="What stands out about this submission?"
                rows={2}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm resize-none"
              />
            </div>
          </div>

          {/* Compliance Checklist */}
          <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-700">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Check className="w-4 h-4" style={{ color: COLORS.lime }} /> Compliance Checklist
            </h3>
            <div className="space-y-3">
              {[
                { key: 'onBrand', label: 'On-Brand', desc: 'Aligns with VB Health brand image and messaging' },
                { key: 'showsProduct', label: 'Shows Product Outcome', desc: 'Clearly demonstrates the product effect' },
                { key: 'claimsCompliant', label: 'Claims Compliant', desc: 'No medical claims or exaggerated promises' },
              ].map(item => (
                <label key={item.key} className="flex items-start gap-3 cursor-pointer group">
                  <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                    review[item.key as keyof typeof review] ? 'border-lime-500 bg-lime-500/20' : 'border-zinc-600 group-hover:border-zinc-500'
                  }`}>
                    {review[item.key as keyof typeof review] && <Check className="w-4 h-4 text-lime-500" />}
                  </div>
                  <input
                    type="checkbox"
                    checked={review[item.key as keyof typeof review] as boolean}
                    onChange={e => setReview({ ...review, [item.key]: e.target.checked })}
                    className="sr-only"
                  />
                  <div>
                    <p className="text-white font-medium">{item.label}</p>
                    <p className="text-zinc-500 text-sm">{item.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Quality Rating */}
          <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-700">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Star className="w-4 h-4 text-yellow-400" /> Quality Rating
            </h3>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map(rating => (
                <button
                  key={rating}
                  onClick={() => setReview({ ...review, qualityRating: rating })}
                  className="text-2xl transition-transform hover:scale-110"
                >
                  {rating <= (review.qualityRating || 0) ? (
                    <span className="text-yellow-400">★</span>
                  ) : (
                    <span className="text-zinc-600">☆</span>
                  )}
                </button>
              ))}
              <span className="ml-3 text-zinc-400 text-sm">
                {review.qualityRating === 1 && 'Poor'}
                {review.qualityRating === 2 && 'Below Average'}
                {review.qualityRating === 3 && 'Average'}
                {review.qualityRating === 4 && 'Good'}
                {review.qualityRating === 5 && 'Excellent'}
              </span>
            </div>
          </div>

          {/* Load Boost Specific Fields */}
          {submission.product === 'Load Boost' && (
            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl p-4 border border-blue-500/30">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                💦 Load Boost Details
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-zinc-400 text-sm mb-2">Consistency Type</label>
                  <select
                    value={loadBoostProfile.consistencyType}
                    onChange={e => setLoadBoostProfile({ ...loadBoostProfile, consistencyType: e.target.value as LoadBoostProfile['consistencyType'] })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="spray-watery">Spray / Watery</option>
                    <option value="thick-ropey">Thick / Ropey</option>
                    <option value="mixed">Mixed</option>
                    <option value="not-visible">Not Visible</option>
                  </select>
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-2">Finish Location</label>
                  <input
                    type="text"
                    value={loadBoostProfile.finishLocation}
                    onChange={e => setLoadBoostProfile({ ...loadBoostProfile, finishLocation: e.target.value })}
                    placeholder="e.g., Face, chest, inside, etc."
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-2">Standout Notes (optional)</label>
                  <textarea
                    value={loadBoostProfile.standoutNotes || ''}
                    onChange={e => setLoadBoostProfile({ ...loadBoostProfile, standoutNotes: e.target.value })}
                    placeholder="What made this clip stand out?"
                    rows={2}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white resize-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Soaking Wet Specific Fields */}
          {submission.product === 'Soaking Wet' && (
            <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 rounded-xl p-4 border border-pink-500/30">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                💦 Soaking Wet Details
              </h3>
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Standout Notes</label>
                <textarea
                  value={loadBoostProfile.standoutNotes || ''}
                  onChange={e => setLoadBoostProfile({ ...loadBoostProfile, standoutNotes: e.target.value })}
                  placeholder="What made this clip stand out? Any notable arousal/squirt effects?"
                  rows={3}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white resize-none"
                />
              </div>
            </div>
          )}

          {/* Male Appearance Assessment */}
          {(gender === 'male' || gender === 'couple') && (
            <div className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 rounded-xl p-4 border border-blue-500/30">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                🍆 Male Appearance
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-zinc-400 text-sm mb-2">How big does his cock look?</label>
                  <select
                    value={maleAppearance.cockSize}
                    onChange={e => setMaleAppearance({ ...maleAppearance, cockSize: e.target.value as MaleAppearanceProfile['cockSize'] })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="small">Small</option>
                    <option value="average">Average</option>
                    <option value="above-average">Above Average</option>
                    <option value="large">Large</option>
                    <option value="huge">Huge</option>
                  </select>
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-2">How hard does he look?</label>
                  <select
                    value={maleAppearance.hardness}
                    onChange={e => setMaleAppearance({ ...maleAppearance, hardness: e.target.value as MaleAppearanceProfile['hardness'] })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="soft">Soft</option>
                    <option value="semi">Semi</option>
                    <option value="hard">Hard</option>
                    <option value="rock-hard">Rock Hard</option>
                  </select>
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-2">Notes</label>
                  <input
                    type="text"
                    value={maleAppearance.notes || ''}
                    onChange={e => setMaleAppearance({ ...maleAppearance, notes: e.target.value })}
                    placeholder="Any other observations"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Gut Reaction Section */}
          <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-700">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-orange-400" /> Gut Reaction
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Would you try this product?</label>
                <select
                  value={review.wouldTryProduct}
                  onChange={e => setReview({ ...review, wouldTryProduct: e.target.value as SubmittedClipReview['wouldTryProduct'] })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="yes">Yes, definitely</option>
                  <option value="maybe">Maybe</option>
                  <option value="no">No</option>
                  <option value="already-use">Already use it</option>
                </select>
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Would you show partner?</label>
                <select
                  value={review.wouldShowPartner}
                  onChange={e => setReview({ ...review, wouldShowPartner: e.target.value as SubmittedClipReview['wouldShowPartner'] })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="yes">Yes</option>
                  <option value="maybe">Maybe</option>
                  <option value="no">No</option>
                  <option value="na">N/A</option>
                </select>
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Would you share with a friend?</label>
                <select
                  value={review.wouldShareWithFriend}
                  onChange={e => setReview({ ...review, wouldShareWithFriend: e.target.value as SubmittedClipReview['wouldShareWithFriend'] })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="yes">Yes</option>
                  <option value="maybe">Maybe</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Would you pay to view this?</label>
                <select
                  value={review.wouldPayToView || 'maybe'}
                  onChange={e => setReview({ ...review, wouldPayToView: e.target.value as 'yes' | 'maybe' | 'no' })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="yes">Yes, worth paying for</option>
                  <option value="maybe">Maybe</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Would you subscribe for more?</label>
                <select
                  value={review.wouldSubscribe || 'maybe'}
                  onChange={e => setReview({ ...review, wouldSubscribe: e.target.value as 'yes' | 'maybe' | 'no' })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="yes">Yes, I'd subscribe</option>
                  <option value="maybe">Maybe</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-2">
                  {gender === 'couple' ? 'Want more from this couple?' : 'Want more from this creator?'}
                </label>
                <select
                  value={review.wantMoreFromCreator || 'maybe'}
                  onChange={e => setReview({ ...review, wantMoreFromCreator: e.target.value as 'yes' | 'maybe' | 'no' })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="yes">Yes, send more!</option>
                  <option value="maybe">Maybe</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            {/* Solo male interest - show when content has a male */}
            {(gender === 'male' || gender === 'couple') && (
              <div className="mt-4">
                <label className="block text-zinc-400 text-sm mb-2">
                  Would you watch solo content from just the male?
                </label>
                <select
                  value={review.wouldWatchMaleSolo || 'maybe'}
                  onChange={e => setReview({ ...review, wouldWatchMaleSolo: e.target.value as 'yes' | 'maybe' | 'no' })}
                  className="w-full md:w-1/3 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="yes">Yes, I'd watch his solo stuff</option>
                  <option value="maybe">Maybe</option>
                  <option value="no">No, prefer couple content</option>
                </select>
              </div>
            )}
            {/* Suggested categories this content fits */}
            <div className="mt-4">
              <label className="block text-zinc-400 text-sm mb-2">
                What categories would this content be perfect for?
              </label>
              <p className="text-zinc-600 text-xs mb-3">Tap all that apply — helps us categorize and encourage creators to submit to the right categories</p>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(cat => {
                  const isSelected = review.suggestedCategories?.includes(cat.id);
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => {
                        const current = review.suggestedCategories || [];
                        if (!isSelected && current.length >= 5) return;
                        setReview({
                          ...review,
                          suggestedCategories: isSelected
                            ? current.filter(id => id !== cat.id)
                            : [...current, cat.id],
                        });
                      }}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        isSelected
                          ? 'bg-lime-500/20 text-lime-300 border border-lime-500/40'
                          : (review.suggestedCategories || []).length >= 5
                            ? 'bg-zinc-800/50 text-zinc-600 border border-zinc-800 cursor-not-allowed'
                            : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-500'
                      }`}
                    >
                      {cat.emoji} {cat.name}
                    </button>
                  );
                })}
              </div>
              {(review.suggestedCategories?.length || 0) > 0 && (
                <p className={`text-xs mt-2 ${review.suggestedCategories!.length >= 5 ? 'text-amber-400' : 'text-zinc-500'}`}>
                  {review.suggestedCategories!.length}/5 selected{review.suggestedCategories!.length >= 5 ? ' (max)' : ''}
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Did you enjoy watching it?</label>
                <select
                  value={review.enjoyedIt || 'neutral'}
                  onChange={e => setReview({ ...review, enjoyedIt: e.target.value as SubmittedClipReview['enjoyedIt'] })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="loved-it">Loved it</option>
                  <option value="enjoyed-it">Enjoyed it</option>
                  <option value="neutral">Neutral</option>
                  <option value="not-for-me">Not for me</option>
                </select>
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Did it arouse you?</label>
                <select
                  value={review.arousedThem || 'not-really'}
                  onChange={e => setReview({ ...review, arousedThem: e.target.value as SubmittedClipReview['arousedThem'] })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="very">Very much</option>
                  <option value="somewhat">Somewhat</option>
                  <option value="not-really">Not really</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-zinc-400 text-sm mb-2">How did it make you feel?</label>
              <textarea
                value={review.howItMadeThemFeel || ''}
                onChange={e => setReview({ ...review, howItMadeThemFeel: e.target.value })}
                placeholder="Describe your reaction - curious, turned on, impressed, indifferent..."
                rows={2}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white resize-none"
              />
            </div>
            <div className="mt-4">
              <label className="block text-zinc-400 text-sm mb-2">What intrigues you about this content?</label>
              <textarea
                value={review.intrigues || ''}
                onChange={e => setReview({ ...review, intrigues: e.target.value })}
                placeholder="What caught your attention — the couple, the outcome, the energy, a specific moment..."
                rows={2}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white resize-none"
              />
              <p className="text-zinc-600 text-xs mt-1">Helps us understand what draws you in so we can find more of what you like</p>
            </div>
            <div className="mt-4">
              <label className="block text-zinc-400 text-sm mb-2">Creator Suggestions (optional)</label>
              <textarea
                value={review.creatorSuggestions || ''}
                onChange={e => setReview({ ...review, creatorSuggestions: e.target.value })}
                placeholder="Suggestions for content improvement (private, not shown to creator)"
                rows={2}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white resize-none"
              />
            </div>
          </div>

          {/* Reviewer Notes */}
          <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-700">
            <h3 className="text-white font-semibold mb-3">Reviewer Notes</h3>
            <textarea
              value={review.notes || ''}
              onChange={e => setReview({ ...review, notes: e.target.value })}
              placeholder="Your overall thoughts on this submission..."
              rows={3}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white resize-none"
            />
          </div>

          {/* Revision Requests (for "Needs Revision" status) */}
          <div className="bg-orange-500/10 rounded-xl p-4 border border-orange-500/30">
            <h3 className="text-orange-400 font-semibold mb-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Revision Requests
            </h3>
            <textarea
              value={review.revisionRequests || ''}
              onChange={e => setReview({ ...review, revisionRequests: e.target.value })}
              placeholder="If the clip needs revisions, describe what changes are needed..."
              rows={3}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white resize-none"
            />
            <p className="text-zinc-500 text-xs mt-2">
              Required if your verdict is “Needs revisions” — say exactly what should change.
            </p>
          </div>

          {/* Auto-generate follow-up suggestions silently (admin-only feature, hidden from reviewers) */}
          {/* Suggestions are computed and saved with the review for admin to use later */}

          {/* Questions for the Creator */}
          <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-700">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-blue-400" /> Questions for Creator
            </h3>
            <p className="text-zinc-500 text-xs mb-3">
              Ask the creator anything about this submission — they can reply directly
            </p>
            {/* Existing questions */}
            {questions.length > 0 && (
              <div className="space-y-3 mb-4">
                {questions.map(q => (
                  <div key={q.id} className="bg-zinc-800 rounded-lg p-3 border border-zinc-700">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-white text-sm">{q.question}</p>
                      <span className="text-zinc-600 text-xs whitespace-nowrap">{new Date(q.askedAt).toLocaleDateString()}</span>
                    </div>
                    {q.reply ? (
                      <div className="mt-2 pl-3 border-l-2 border-blue-500/40">
                        <p className="text-blue-300 text-sm">{q.reply}</p>
                        <span className="text-zinc-600 text-xs">
                          Creator replied {new Date(q.repliedAt!).toLocaleDateString()}
                        </span>
                      </div>
                    ) : (
                      <p className="text-zinc-600 text-xs mt-1 italic">Awaiting reply...</p>
                    )}
                  </div>
                ))}
              </div>
            )}
            {/* New question input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newQuestion}
                onChange={e => setNewQuestion(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && newQuestion.trim()) {
                    setQuestions([...questions, {
                      id: generateId('rq'),
                      reviewerId,
                      question: newQuestion.trim(),
                      askedAt: new Date().toISOString(),
                    }]);
                    setNewQuestion('');
                  }
                }}
                placeholder="Type a question and press Enter..."
                className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
              />
              <button
                onClick={() => {
                  if (!newQuestion.trim()) return;
                  setQuestions([...questions, {
                    id: generateId('rq'),
                    reviewerId,
                    question: newQuestion.trim(),
                    askedAt: new Date().toISOString(),
                  }]);
                  setNewQuestion('');
                }}
                disabled={!newQuestion.trim()}
                className="px-3 py-2 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-lg hover:bg-blue-500/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Already Promoted Notice */}
          {isPromoted && (
            <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
              <p className="text-green-400 flex items-center gap-2">
                <Check className="w-4 h-4" /> This clip has been added to the DateNite library
              </p>
            </div>
          )}

          {!canSeeCommunityFeedback && otherReviews.length > 0 && (
            <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-700">
              <h3 className="text-white font-semibold mb-1">Community feedback locked</h3>
              <p className="text-zinc-500 text-sm">
                {otherReviews.length} other review{otherReviews.length !== 1 ? 's are' : ' is'} waiting. Save your blind review to unlock them.
              </p>
            </div>
          )}

          {canSeeCommunityFeedback && (
            <div className="space-y-4">
              <div className={`rounded-xl p-4 border ${
                needsPostRevealReflection
                  ? 'bg-amber-500/10 border-amber-500/40'
                  : 'bg-purple-500/10 border-purple-500/30'
              }`}>
                <h3 className={`font-semibold mb-1 ${needsPostRevealReflection ? 'text-amber-300' : 'text-purple-300'}`}>
                  {needsPostRevealReflection ? 'Follow-up required: community feedback unlocked' : 'Community feedback unlocked'}
                </h3>
                <p className="text-zinc-300 text-sm">
                  {needsPostRevealReflection
                    ? 'Your blind review is saved. Read the other reviewer notes below, then save whether seeing them changed how you feel.'
                    : 'Your review is saved. Compare notes below, then record whether seeing other reviewers changed how you feel.'
                  }
                </p>
              </div>

              <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-700">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  Other Reviews ({otherReviews.length})
                </h3>
                {otherReviews.length === 0 ? (
                  <p className="text-zinc-500 text-sm">No other reviewers have weighed in yet.</p>
                ) : (
                  <div className="space-y-3">
                    {otherReviews.map((rev, i) => (
                      <div key={i} className="bg-zinc-800 rounded-lg p-3 border border-zinc-700">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-white text-sm font-medium">{rev.reviewerId}</span>
                          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs">
                            Quality {rev.qualityRating}/5
                          </span>
                          {rev.reviewedAt && (
                            <span className="text-zinc-500 text-xs">{new Date(rev.reviewedAt).toLocaleDateString()}</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${rev.onBrand ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                            {rev.onBrand ? 'On Brand' : 'Not On Brand'}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs ${rev.showsProduct ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                            {rev.showsProduct ? 'Shows Product' : 'No Product'}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs ${rev.claimsCompliant ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                            {rev.claimsCompliant ? 'Claims OK' : 'Claims Issue'}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-1.5 text-xs mb-2">
                          <div className="bg-zinc-900/50 rounded px-2 py-1">
                            <span className="text-zinc-500">Try:</span> <span className="text-white capitalize">{rev.wouldTryProduct?.replace(/-/g, ' ')}</span>
                          </div>
                          <div className="bg-zinc-900/50 rounded px-2 py-1">
                            <span className="text-zinc-500">Show:</span> <span className="text-white capitalize">{rev.wouldShowPartner?.replace(/-/g, ' ')}</span>
                          </div>
                          <div className="bg-zinc-900/50 rounded px-2 py-1">
                            <span className="text-zinc-500">Share:</span> <span className="text-white capitalize">{rev.wouldShareWithFriend?.replace(/-/g, ' ')}</span>
                          </div>
                        </div>
                        {rev.enjoyedIt && <p className="text-zinc-400 text-xs">Enjoyment: <span className="text-white capitalize">{rev.enjoyedIt.replace(/-/g, ' ')}</span></p>}
                        {rev.arousedThem && <p className="text-zinc-400 text-xs">Arousal: <span className="text-white capitalize">{rev.arousedThem.replace(/-/g, ' ')}</span></p>}
                        {rev.howItMadeThemFeel && <p className="text-zinc-300 text-xs mt-1">"{rev.howItMadeThemFeel}"</p>}
                        {rev.intrigues && <p className="text-zinc-300 text-xs mt-1">Intrigued by: "{rev.intrigues}"</p>}
                        {rev.notes && <p className="text-zinc-300 text-xs mt-1">Notes: "{rev.notes}"</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {otherReviews.length > 0 && (
                <div className="bg-purple-500/10 rounded-xl p-4 border border-purple-500/30">
                  <h3 className="text-purple-300 font-semibold mb-2">Did seeing other reviews change anything?</h3>
                  <p className="text-zinc-400 text-sm mb-4">
                    Capture the difference between your first reaction and your reaction after seeing the room.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
                    {[
                      { value: 'same', label: 'No change', desc: 'My original take still stands' },
                      { value: 'somewhat', label: 'A little', desc: 'I noticed something new' },
                      { value: 'changed', label: 'Yes', desc: 'I see it differently now' },
                    ].map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setReview({ ...review, postRevealChangedMind: option.value as SubmittedClipReview['postRevealChangedMind'] })}
                        className={`text-left rounded-xl border p-3 transition-colors ${
                          review.postRevealChangedMind === option.value
                            ? 'bg-purple-500/20 border-purple-400 text-white'
                            : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-zinc-500'
                        }`}
                      >
                        <p className="font-medium text-sm">{option.label}</p>
                        <p className="text-xs text-zinc-500 mt-1">{option.desc}</p>
                      </button>
                    ))}
                  </div>

                  {(review.postRevealChangedMind === 'somewhat' || review.postRevealChangedMind === 'changed') && (
                    <div className="mb-4">
                      <label className="block text-zinc-400 text-sm mb-2">Would you change your rating?</label>
                      <div className="flex items-center gap-2">
                        {[1, 2, 3, 4, 5].map(rating => (
                          <button
                            key={rating}
                            type="button"
                            onClick={() => setReview({ ...review, postRevealRating: rating })}
                            className="text-xl transition-transform hover:scale-110"
                          >
                            {rating <= (review.postRevealRating || review.qualityRating || 0) ? (
                              <span className="text-purple-300">★</span>
                            ) : (
                              <span className="text-zinc-600">☆</span>
                            )}
                          </button>
                        ))}
                        <span className="text-zinc-500 text-xs ml-2">
                          Original: {review.qualityRating || 0}/5
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="mb-4">
                    <label className="block text-zinc-400 text-sm mb-2">What changed or stayed firm?</label>
                    <textarea
                      value={review.postRevealNotes || ''}
                      onChange={e => setReview({ ...review, postRevealNotes: e.target.value })}
                      placeholder="Example: I still liked it, but another reviewer made me notice the product outcome was clearer than I first thought..."
                      rows={3}
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white resize-none"
                    />
                  </div>

                  <div className="mb-4">
                    <p className="text-zinc-400 text-sm mb-2">Why?</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        'noticed-something',
                        'well-explained',
                        'more-appealing',
                        'less-sure',
                        'still-disagree',
                        'other',
                      ].map(reason => {
                        const labels: Record<string, string> = {
                          'noticed-something': 'I noticed something I missed',
                          'well-explained': 'Another reviewer explained it well',
                          'more-appealing': 'Group reaction made it more appealing',
                          'less-sure': 'Group reaction made me less sure',
                          'still-disagree': 'I still disagree',
                          other: 'Other',
                        };
                        const selected = review.postRevealReasons?.includes(reason);
                        return (
                          <button
                            key={reason}
                            type="button"
                            onClick={() => {
                              const current = review.postRevealReasons || [];
                              setReview({
                                ...review,
                                postRevealReasons: selected
                                  ? current.filter(r => r !== reason)
                                  : [...current, reason],
                              });
                            }}
                            className={`px-3 py-1.5 rounded-full text-xs border ${
                              selected
                                ? 'bg-purple-500/20 text-purple-300 border-purple-400/60'
                                : 'bg-zinc-900 text-zinc-400 border-zinc-700 hover:border-zinc-500'
                            }`}
                          >
                            {labels[reason]}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <button
                    onClick={handleSavePostRevealReaction}
                    disabled={savingReflection}
                    className={`px-4 py-2 rounded-xl font-semibold text-zinc-900 disabled:opacity-60 disabled:cursor-wait ${needsPostRevealReflection ? 'animate-pulse' : ''}`}
                    style={{ backgroundColor: COLORS.lime }}
                  >
                    {savingReflection ? 'Saving...' : hasSavedPostRevealReaction ? 'Update Reflection' : 'Save Required Reflection'}
                  </button>
                  {hasSavedPostRevealReaction && !savingReflection && (
                    <span className="ml-3 text-lime-400 text-sm" role="status">Reflection saved</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Verdict + actions stay visible while scrolling the form */}
        <div className="border-t border-zinc-700 bg-zinc-900/95 p-4 flex-shrink-0 space-y-3">
          <div role="group" aria-label="Your verdict" className="space-y-2">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <span className="text-sm font-semibold text-white">Your verdict</span>
              <span className="text-xs text-zinc-500">The admin makes the final call using everyone’s verdicts.</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {(['approve', 'revise', 'reject'] as const).map(v => (
                <button
                  key={v}
                  onClick={() => setVerdict(v)}
                  aria-pressed={review.verdict === v}
                  className={`px-2 py-2 rounded-lg border text-sm font-semibold transition-colors ${review.verdict === v ? VERDICTS[v].chip : 'border-zinc-600 text-zinc-400 hover:text-white'}`}
                >
                  {review.verdict === v && <Check className="w-3.5 h-3.5 inline mr-1" />}{VERDICTS[v].label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleSaveDraft}
              disabled={savingReview}
              className="px-4 py-2 bg-zinc-700 text-white rounded-xl hover:bg-zinc-600 transition-colors disabled:opacity-60"
            >
              Save draft
            </button>
            <button
              onClick={handleSubmitReview}
              disabled={savingReview}
              className="flex-1 sm:flex-none px-6 py-2 rounded-xl font-semibold text-zinc-900 disabled:opacity-60"
              style={{ backgroundColor: COLORS.lime }}
            >
              {savingReview ? 'Saving…' : currentSavedReview?.verdict ? 'Update review' : 'Submit review'}
            </button>
            {saveError && (
              <p className="text-red-400 text-sm basis-full" role="alert">{saveError}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
